import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore, isAdmin } from '@/store/useAuthStore'
import { useFeatureFlagStore } from '@/store/useFeatureFlagStore'
import { useUIStore } from '@/store/uiStore'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'

export function ProtectedRoute() {
  const { user, profile, isAuthenticated, isLoading } = useAuthStore()
  const featureFlags = useFeatureFlagStore((s) => s.flags)
  const flagsLoaded = useFeatureFlagStore((s) => s.loaded)
  const location = useLocation()
  const showToast = useUIStore((s) => s.showToast)

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      showToast('Please sign in to access this page', 'error')
    }
  }, [isLoading, isAuthenticated, showToast])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isAuthenticated) {
    // Store intended destination for redirect after login
    return <Navigate to="/" state={{ from: location, requiresAuth: true }} replace />
  }

  // Email verification gate — admins/demo are exempt so internal tooling
  // doesn't break. Everyone else is bounced to the verify-email screen until
  // they click the link in their inbox. Admin-toggleable via the
  // emailVerificationRequired feature flag; while flags are still loading we
  // hold on a spinner rather than bouncing on the cautious default.
  const isAdminUser = isAdmin(user?.email, profile)
  if (!user?.emailVerified && !isAdminUser && location.pathname !== '/verify-email') {
    if (!flagsLoaded) {
      return (
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner size="lg" />
        </div>
      )
    }
    if (featureFlags.emailVerificationRequired) {
      return <Navigate to="/verify-email" replace />
    }
  }

  return <Outlet />
}

export function AdminRoute() {
  const { user, profile, isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isAuthenticated || !isAdmin(user?.email, profile)) {
    return <Navigate to="/" replace />
  }

  return <Outlet />
}
